import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, FileText } from "lucide-react";
import { adminApi, imgUrl } from "../../lib/api";
import { toast } from "sonner";

export default function AdminUploads() {
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = () => adminApi.get("/uploads").then((r) => setUploads(r.uploads));
  useEffect(() => { load().catch(() => {}); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      await adminApi.upload(f);
      toast.success("File uploaded.");
      load();
    } catch {
      toast.error("Upload failed (only PDF/JPG/PNG/WebP up to 25MB).");
    }
    setBusy(false);
    e.target.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    await adminApi.delete(`/uploads/${id}`);
    load();
  };

  return (
    <div data-testid="admin-uploads-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-zinc-900">Media Library</h1>
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid="upload-file-btn"
          className="inline-flex items-center gap-1.5 bg-zinc-900 text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-zinc-700 disabled:opacity-60">
          <Upload className="w-4 h-4" /> {busy ? "Uploading..." : "Upload"}
        </button>
        <input ref={ref} type="file" accept=".pdf,image/*" className="hidden" onChange={upload} data-testid="upload-file-input" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {uploads.map((u) => (
          <div key={u.id} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={`upload-card-${u.id}`}>
            {u.content_type === "application/pdf" ? (
              <div className="h-32 flex items-center justify-center bg-zinc-100"><FileText className="w-10 h-10 text-zinc-400" /></div>
            ) : (
              <img src={imgUrl(u.url)} alt={u.filename} className="h-32 w-full object-cover" loading="lazy" />
            )}
            <div className="p-3">
              <p className="text-xs font-medium text-zinc-900 truncate" title={u.filename}>{u.filename}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">{(u.size / 1024).toFixed(0)} KB · {(u.uploaded_at || "").slice(0, 10)}</p>
              {u.used_by?.length > 0 && <p className="text-[10px] text-zinc-500 mt-1 truncate" title={u.used_by.join(", ")}>{u.used_by.join(", ")}</p>}
              <div className="flex justify-between items-center mt-2">
                <button onClick={() => { navigator.clipboard.writeText(u.url); toast.success("URL copied"); }} data-testid={`copy-url-${u.id}`}
                  className="text-[10px] font-semibold text-zinc-500 hover:text-zinc-900">Copy URL</button>
                <button onClick={() => remove(u.id)} data-testid={`delete-upload-${u.id}`} className="text-zinc-400 hover:text-red-600" aria-label="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!uploads.length && <p className="text-sm text-zinc-500 text-center py-10">No uploads yet.</p>}
    </div>
  );
}
